import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Loader2, UserPlus, Building2, Briefcase, Megaphone,
  Truck, Users, ListChecks, FileText, BookOpen, ChevronDown,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { searchAPI } from '@/lib/api';
import useDebouncedValue from '@/hooks/useDebouncedValue';

// Icon per entity type so each group is scannable at a glance.
const GROUP_ICON = {
  lead: UserPlus,
  account: Building2,
  project: Briefcase,
  campaign: Megaphone,
  vendor: Truck,
  client: Users,
  task: ListChecks,
  order: FileText,
  knowledge: BookOpen,
};

// Global header search. Wired to GET /api/v1/search — every group is already
// permission-scoped server-side (see SearchService), so this only renders what
// the caller may see. Navigates to the backend-provided `url`, so list-state
// preservation on the destination page keeps working unchanged.
export default function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelW, setPanelW] = useState(720); // measured to fit the content area
  const inputRef = useRef(null);
  const rootRef = useRef(null);

  const trimmed = query.trim();
  const dq = useDebouncedValue(trimmed, 300);
  const enabled = dq.length >= 2; // <2 chars → never calls the API

  const { data, isFetching } = useQuery({
    queryKey: ['global-search', dq],
    queryFn: () => searchAPI.global(dq, 5),
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev, // keep prior hits visible while fetching (v5)
  });

  const groups = enabled ? (data?.groups || []) : [];
  // Group types the user has collapsed — hidden from view AND from keyboard nav.
  const [collapsed, setCollapsed] = useState([]);
  const collapsedSet = useMemo(() => new Set(collapsed), [collapsed]);
  const toggleGroup = useCallback((type) => {
    setCollapsed((c) => (c.includes(type) ? c.filter((t) => t !== type) : [...c, type]));
    setActiveIndex(0);
    inputRef.current?.focus();
  }, []);

  // Only items from EXPANDED groups are navigable, so ↑/↓/↵ skip hidden ones.
  const flatItems = useMemo(
    () => groups.flatMap((g) => (collapsedSet.has(g.type)
      ? []
      : g.items.map((it) => ({ ...it, _group: g.type })))),
    [groups, collapsedSet],
  );

  // Reset highlight whenever the query (and therefore the result set) changes.
  useEffect(() => { setActiveIndex(0); }, [dq]);

  const showDropdown = open && enabled;

  const select = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
    navigate(item.url);
  }, [navigate]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(flatItems.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      const it = flatItems[activeIndex];
      if (it) { e.preventDefault(); select(it); }
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  // ⌘K / Ctrl-K focuses the box from anywhere.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Close when clicking outside.
  useEffect(() => {
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keep the highlighted row in view during keyboard nav.
  useEffect(() => {
    if (!showDropdown) return;
    const el = rootRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, showDropdown]);

  // Size the panel to the space between the content-area left (header's left,
  // i.e. the sidebar's right edge) and the search pill's right edge, so it can
  // never slide under the sidebar or off the viewport at any width. Falls back
  // to the viewport when there is no header ancestor.
  useEffect(() => {
    if (!showDropdown) return undefined;
    const measure = () => {
      const root = rootRef.current;
      if (!root) return;
      const pillRight = root.getBoundingClientRect().right;
      const header = root.closest('header');
      const leftBound = header ? header.getBoundingClientRect().left : 8;
      const avail = pillRight - leftBound - 12; // 12px breathing room
      setPanelW(Math.max(280, Math.min(860, Math.floor(avail))));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [showDropdown]);

  const twoCols = panelW >= 680; // enough width for a comfortable 2-column grid
  // Presence of results is based on the GROUPS, not on how many are expanded —
  // collapsing every group must still show the headers (so they can be reopened),
  // never the empty state. `flatItems` (collapse-aware) is only for keyboard nav.
  const hasGroups = groups.length > 0;
  const isEmpty = enabled && !isFetching && !hasGroups;
  let counter = -1; // running flat index shared by keyboard + mouse highlight

  return (
    <div ref={rootRef} className="relative hidden md:block">
      <div className="flex items-center gap-2 bg-secondary rounded-full px-4 py-2 text-sm text-muted-foreground w-72">
        <Search size={16} strokeWidth={2} />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search tasks, projects, leads, vendors…"
          aria-label="Global search"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls="global-search-listbox"
          className="bg-transparent outline-none focus:outline-none focus-visible:outline-none flex-1 placeholder:text-muted-foreground text-foreground"
        />
        {isFetching && enabled && <Loader2 size={14} className="animate-spin shrink-0" />}
      </div>

      {showDropdown && (
        <div
          style={{ width: panelW }}
          className="absolute right-0 mt-3 max-w-[96vw] rounded-2xl border border-border bg-card shadow-2xl z-50 overflow-hidden"
        >
          {/* Header bar */}
          <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-border/60 bg-secondary/30">
            <div className="text-sm text-muted-foreground truncate">
              {isFetching && !hasGroups
                ? 'Searching…'
                : isEmpty
                  ? 'No matches'
                  : (<>Results for <span className="font-semibold text-foreground">“{dq}”</span></>)}
            </div>
            <div className="hidden sm:flex items-center gap-3 text-[11px] text-muted-foreground/70 shrink-0">
              <span><kbd className="font-sans">↑↓</kbd> navigate</span>
              <span><kbd className="font-sans">↵</kbd> open</span>
              <span><kbd className="font-sans">esc</kbd> close</span>
            </div>
          </div>

          {/* Body */}
          <div id="global-search-listbox" role="listbox" className="max-h-[70vh] overflow-auto p-4">
            {isFetching && !hasGroups && (
              <div className="flex items-center gap-2 px-3 py-10 justify-center text-sm text-muted-foreground">
                <Loader2 size={16} className="animate-spin" /> Searching…
              </div>
            )}

            {isEmpty && (
              <div className="flex flex-col items-center justify-center gap-1 px-3 py-12 text-center">
                <Search size={22} className="text-muted-foreground/50" />
                <div className="text-sm font-medium text-foreground">No results for “{dq}”</div>
                <div className="text-xs text-muted-foreground">Try a name, code, FL number, or ID.</div>
              </div>
            )}

            {hasGroups && (
              <div className={`grid gap-x-6 gap-y-5 ${twoCols ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {groups.map((g) => {
                  const Icon = GROUP_ICON[g.type] || Search;
                  const isCollapsed = collapsedSet.has(g.type);
                  return (
                    <section key={g.type} className="min-w-0">
                      {/* Click the header to collapse/expand this group so you can
                          focus on one type. onMouseDown-preventDefault keeps input
                          focus for continued keyboard navigation. */}
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => toggleGroup(g.type)}
                        aria-expanded={!isCollapsed}
                        title={isCollapsed ? `Show ${g.label}` : `Hide ${g.label}`}
                        className="w-full flex items-center gap-2 px-1 pb-2 mb-1 border-b border-border/50 group/hd"
                      >
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
                          <Icon size={13} strokeWidth={2.2} />
                        </span>
                        <span className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                          {g.label}
                        </span>
                        <span className="ml-auto text-[11px] font-medium text-muted-foreground/60">
                          {g.items.length}
                        </span>
                        <ChevronDown
                          size={14}
                          className={`text-muted-foreground/60 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                        />
                      </button>
                      {!isCollapsed && (
                        <div className="space-y-0.5">
                          {g.items.map((it) => {
                            counter += 1;
                            const idx = counter;
                            const active = idx === activeIndex;
                            return (
                              <button
                                type="button"
                                key={`${g.type}-${it.id}`}
                                data-idx={idx}
                                role="option"
                                aria-selected={active}
                                onMouseEnter={() => setActiveIndex(idx)}
                                onClick={() => select(it)}
                                className={`w-full text-left px-3 py-2 rounded-lg flex flex-col min-w-0 transition-colors ${active ? 'bg-primary/10' : 'hover:bg-secondary/70'}`}
                              >
                                <span className="text-sm text-foreground truncate">{it.title}</span>
                                {it.subtitle && (
                                  <span className="text-xs text-muted-foreground truncate">{it.subtitle}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
